import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// A short message at the bottom of the screen that fades on its own: the
// repost confirmation, a list saved to Apple Music.
export const Toast = styled.div<{ $visible: boolean }>`
  position: fixed;
  /* Above the player bar, home indicator included. */
  bottom: calc(5rem + env(safe-area-inset-bottom, 0px));
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100vw - 2rem);
  width: max-content;
  text-align: center;
  background: #333;
  color: white;
  padding: 0.6rem 1.25rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.4;
  z-index: 1300;
  pointer-events: none;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const ToastLink = styled.a`
  margin-left: 0.75em;
  color: white;
  font-weight: 800;
  text-decoration: underline;
  white-space: nowrap;
  /* The toast lets taps through to the page under it; only its link takes them. */
  pointer-events: auto;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// Shown while the work the toast reports on is still going.
const Spinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 0.6em;
  vertical-align: -1px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export interface ToastAction {
  label: string;
  href: string;
}

/** `show(message, ms, action)` puts a toast up, with an optional link that
 *  opens in a new tab; `busy(message)` puts one up with a spinner until the
 *  next show or hide; `element` renders it (or nothing). */
export function useToast() {
  const [message, setMessage] = useState<string | null>(null);
  const [action, setAction] = useState<ToastAction | null>(null);
  const [isBusy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  function show(next: string, ms = 2600, nextAction?: ToastAction) {
    if (timer.current) clearTimeout(timer.current);
    setMessage(next);
    setAction(nextAction || null);
    setBusy(false);
    timer.current = setTimeout(() => setMessage(null), ms);
  }
  function busy(next: string) {
    if (timer.current) clearTimeout(timer.current);
    setMessage(next);
    setAction(null);
    setBusy(true);
  }
  function hide() {
    if (timer.current) clearTimeout(timer.current);
    setMessage(null);
    setBusy(false);
  }
  const element = message ? (
    <Toast $visible role="status" aria-busy={isBusy}>
      {isBusy && <Spinner aria-hidden="true" />}
      {message}
      {action && (
        <ToastLink href={action.href} target="_blank" rel="noopener noreferrer">
          {action.label}
        </ToastLink>
      )}
    </Toast>
  ) : null;
  return { show, busy, hide, element };
}
